import React, { useState } from 'react';
import { Users, TrendingUp } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { saveMultipleJobs } from '../../redux/SavedJobSlice/savedJobSlice';
import { saveRecruiterContacts } from '../../redux/ContactSlice/contactSlice';
import CompanyCard from './CompanyCard';
import CompanyInsightsPanel from './JobContent/CompanyInsightsPanel';

const RightPanelTabs = ({ companies, onViewCompany, onDeleteCompany, activeTab, onTabChange, selectedCompany, onCloseCompany, setSelectedJobs, handleSaveJobs }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const savedJobs = useSelector((state) => state.savedJobs.savedJobs);
    const [savingJobs, setSavingJobs] = useState({});
    const [savedJobsMap, setSavedJobsMap] = useState({});
    
    const onSave = async (company) => {
        console.log("Company data found in the right panel save button", company);
        
        const job = company.jobData;
        const recruiterData = company.recruiterData;
        
        if (!job || !user) {
            console.error('❌ Cannot save job: Missing job or user data');
            return;
        }
        
        // Check if already saved
        const isAlreadySaved = savedJobs.some(savedJob => 
            savedJob.jobId?._id === job._id || savedJob.jobId === job._id
        );
        
        if (isAlreadySaved || savedJobsMap[job._id]) {
            console.log('ℹ️ Job already saved');
            return;
        }
        
        setSavingJobs(prev => ({ ...prev, [job._id]: true }));
        
        try {
            console.log('💾 Saving job from right panel:', job.companyName);
            
            const jobToSave = {
                jobId: job._id,
                companyId: job.companyId || company.organizationId,
                companyName: job.companyName,
            };
            
            const recruiterDataToSend = recruiterData ? { [job._id]: recruiterData } : undefined;
            
            const saved = await dispatch(
                saveMultipleJobs({
                    userId: user._id,
                    jobs: [jobToSave],
                    recruiterData: recruiterDataToSend,
                })
            ).unwrap();
            
            const { insertedJobs } = saved;
            
            if (insertedJobs && insertedJobs.length > 0) {
                const insertedJob = insertedJobs[0];
                const savedOrgId = insertedJob.organizationId;
                const savedCompanyId = insertedJob.companyId || job.companyId;
                
                // Save recruiter contacts if we have the data
                if (recruiterData && savedOrgId) {
                    console.log('💾 Saving recruiter contacts to DB');
                    
                    const allContacts = [
                        ...(recruiterData.hiring_manager?.best_guesses || []),
                        ...(recruiterData.recruiters?.best_guesses || [])
                    ];
                    
                    if (allContacts.length > 0) {
                        await dispatch(saveRecruiterContacts({
                            jobId: job._id,
                            companyId: savedCompanyId,
                            organizationId: savedOrgId,
                            recruiterData: recruiterData
                        })).unwrap();
                        
                        console.log('✅ Job and recruiters saved successfully');
                    }
                }
                
                console.log('✅ Job saved successfully from right panel');
                setSavedJobsMap(prev => ({ ...prev, [job._id]: true }));
            }
        } catch (error) {
            console.error('❌ Error saving job from right panel:', error);
        } finally {
            setSavingJobs(prev => ({ ...prev, [job._id]: false }));
        }
    };
    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Tab Headers */}
            <div className="flex border-b border-gray-200 bg-white">
                <button
                    onClick={() => onTabChange('recruiters')}
                    className={`flex-1 px-4 py-3 font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                        activeTab === 'recruiters'
                            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                    <Users size={18} />
                    Referral Analysis
                    {companies.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            {companies.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => onTabChange('insights')}
                    className={`flex-1 px-4 py-3 font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                        activeTab === 'insights'
                            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                    <TrendingUp size={18} />
                    Company Insights
                </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
                {activeTab === 'recruiters' ? (
                    <div className="space-y-3 p-4">
                        {companies.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="mx-auto text-gray-300 mb-3" size={48} />
                                <p className="text-gray-500 font-medium">No companies fetched yet</p>
                                <p className="text-sm text-gray-400 mt-1">
                                    Click "Show Referral Path" on a job to start
                                </p>
                            </div>
                        ) : (
                            companies.map((company) => (
                                <CompanyCard
                                    key={company.companyKey || company.id}
                                    company={company}
                                    onView={() => onViewCompany(company)}
                                    onSave={onSave}
                                    onDelete={onDeleteCompany}
                                    saving={savingJobs[company.jobData?._id]}
                                    saved={savedJobsMap[company.jobData?._id] || savedJobs.some(savedJob => 
                                        savedJob.jobId?._id === company.jobData?._id || savedJob.jobId === company.jobData?._id
                                    )}
                                />
                            ))
                        )}
                    </div>
                ) : (
                    // Company Insights Tab - Show selected company or empty state
                    selectedCompany ? (
                        <CompanyInsightsPanel 
                            company={selectedCompany} 
                            onClose={onCloseCompany}
                        />
                    ) : (
                        <div className="text-center py-12 p-4">
                            <TrendingUp className="mx-auto text-gray-300 mb-3" size={48} />
                            <p className="text-gray-500 font-medium">Company Insights</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Click on a job to view company insights
                            </p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default RightPanelTabs;
